import { type ReactElement } from "react";

import { styled } from "simplydo/react/styled";
import type { UiColor } from "simplydo/react/theme";

import { Dropdown, type DropdownProps } from "../dropdown";
import { ColorSwatch } from "./ColorSwatch";
import {
  UI_COLOR_CHOOSER_OPTIONS,
  uiColorName,
  type UiColorChooserOption,
} from "./colorChooserOptions";

const ColorLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export type ColorChooserProps = {
  value?: UiColor | string | null;
  onChange: (color: UiColor) => void;
  options?: readonly UiColorChooserOption[];
  placeholder?: string;
} & Pick<
  DropdownProps,
  | "trigger"
  | "clearable"
  | "disabled"
  | "fluid"
  | "size"
  | "variant"
  | "style"
  | "id"
>;

export function ColorChooser({
  value,
  trigger,
  onChange,
  placeholder = "Choose a colour",
  clearable,
  disabled,
  fluid,
  size,
  variant = "outline",
  style,
  id,
}: ColorChooserProps): ReactElement {
  const dropdownOptions = UI_COLOR_CHOOSER_OPTIONS.map((color) => ({
    key: color,
    value: color,
    text: uiColorName(color),
    children: (
      <ColorLabel>
        <ColorSwatch color={color} size={14} />
        {uiColorName(color)}
      </ColorLabel>
    ),
  }));

  return (
    <Dropdown
      id={id}
      trigger={trigger}
      fluid={fluid}
      size={size}
      variant={variant}
      style={style}
      clearable={clearable}
      disabled={disabled}
      placeholder={placeholder}
      value={value ?? undefined}
      options={dropdownOptions}
      onChange={(_e, { value: nextValue }) => {
        onChange(nextValue as UiColor);
      }}
    />
  );
}
